import MAP_DATA from '../../_json/_map_data.json';
import { DEBUG_MODE, IS_PRODUCTION_DOMAIN } from '../_DebugConfig';

/**
 * Unified AR Service for handling all AR-related functionality
 */
class ARService
{
    /**
     * Check if a building has AR capability
     * @param {number} buildingId - Building ID to check
     * @returns {boolean} True if building has AR
     */
    static hasAR(buildingId)
    {
        return MAP_DATA.ar?.some(arItem => arItem.id === buildingId) || false;
    }

    /**
     * Get AR URL for a building
     * @param {number} buildingId - Building ID
     * @param {string} lang - Language code ('ja' or other)
     * @param {boolean} useStaging - Whether to use staging URLs (default: false)
     * @returns {string|null} AR URL or null if not available
     */
    static getARUrl(buildingId, lang, useStaging = false)
    {
        const arItem = MAP_DATA.ar?.find(ar => ar.id === buildingId);
        if (!arItem) return null;

        if (useStaging)
        {
            // Use staging URLs
            return lang === 'ja' ? arItem.stg_url_ja : arItem.stg_url_en;
        }
        else
        {
            // Use production URLs
            return lang === 'ja' ? arItem.url_ja : arItem.url_en;
        }
    }

    /**
     * Get all buildings that have AR capability
     * @returns {number[]} Array of building IDs that have AR
     */
    static getARBuildingIds()
    {
        return MAP_DATA.ar?.map(arItem => arItem.id) || [];
    }

    /**
     * Get AR data for a specific building
     * @param {number} buildingId - Building ID
     * @returns {Object|null} AR data object or null if not found
     */
    static getARData(buildingId)
    {
        return MAP_DATA.ar?.find(ar => ar.id === buildingId) || null;
    }

    /**
     * Filter buildings that have AR capability
     * @param {Array} buildings - Array of building objects
     * @returns {Array} Filtered array of buildings with AR
     */
    static filterARBuildings(buildings)
    {
        const arBuildingIds = this.getARBuildingIds();
        return buildings.filter(building => arBuildingIds.includes(building.id));
    }

    /**
     * Add AR property to buildings for proximity manager compatibility
     * @param {Array} buildings - Array of building objects
     * @returns {Array} Buildings with ar property added
     */
    static addARPropertyToBuildings(buildings)
    {
        const arBuildingIds = this.getARBuildingIds();
        return buildings.map(building => ({
            ...building,
            ar: arBuildingIds.includes(building.id)
        }));
    }

    /**
     * Open AR URL in new tab and handle staging environment
     * @param {number} buildingId - Building ID
     * @param {string} lang - Language code
     * @param {Function} onSuccess - Callback when AR is successfully opened
     */
    static openAR(buildingId, lang, onSuccess = null)
    {
        // Determine if we should use staging URLs
        const useStaging = !IS_PRODUCTION_DOMAIN && DEBUG_MODE;
        
        const arUrl = this.getARUrl(buildingId, lang, useStaging);
        if (!arUrl) 
        {
            console.warn(`No AR URL found for building ${buildingId}`);
            return false;
        }

        try 
        {
            window.open(arUrl, '_blank');
            if (onSuccess) onSuccess();
            return true;
        } 
        catch (error) 
        {
            console.error('Failed to open AR URL:', error);
            return false;
        }
    }

    /**
     * Check if AR should be enabled based on environment
     * @returns {boolean} True if AR should be enabled
     */
    static isAREnabled()
    {
        // AR is always enabled, but URLs may differ based on environment
        return true;
    }

    /**
     * Get AR configuration for environment
     * @returns {Object} AR configuration object
     */
    static getARConfig()
    {
        return {
            enabled: this.isAREnabled(),
            useStaging: !IS_PRODUCTION_DOMAIN && DEBUG_MODE,
            totalARBuildings: MAP_DATA.ar?.length || 0
        };
    }
}

export default ARService; 